(() => {
    const readProductMainConfig = (root) => {
      const productMainConfigElement = root?.querySelector('[data-product-main-config]') || document.querySelector('[data-product-main-config]');
      if (!productMainConfigElement) return {};

      try {
        return JSON.parse(productMainConfigElement.textContent || '{}');
      } catch (error) {
        return {};
      }
    };

    const ThemeEvents = {
      variantSelected: 'variant:selected',
      variantUpdate: 'variant:update',
    };
    const productLifetimes = new WeakMap();
    const pricingRefreshers = new WeakMap();
    const stickyTopReviewLabelObserverMap = new WeakMap();
    const videoStoryFullscreenCleanupMap = new WeakMap();

    const initStickyReviewLabelFit = (root, signal) => {
      const reviews = root.querySelector('.sb-product-main__sticky-bottom-reviews');
      const reviewCount = reviews?.querySelector('.sb-product-main__sticky-bottom-review-count');
      const fullLabel = reviewCount?.querySelector('.sb-product-main__sticky-bottom-review-count-full');
      const stars = reviews?.querySelector('.sb-product-main__sticky-bottom-stars');
      if (!reviews || !reviewCount || !fullLabel || !stars) return;

      let frameId = 0;
      const update = () => {
        frameId = 0;
        reviewCount.classList.remove('sb-product-main__sticky-bottom-review-count--compact');
        const fullLabelStyle = getComputedStyle(fullLabel);
        const reviewsStyle = getComputedStyle(reviews);
        const gap = Number.parseFloat(reviewsStyle.columnGap || reviewsStyle.gap || '0') || 0;
        const requiredWidth = stars.getBoundingClientRect().width + gap + fullLabel.scrollWidth;
        reviewCount.classList.toggle(
          'sb-product-main__sticky-bottom-review-count--compact',
          fullLabelStyle.display === 'none' || requiredWidth > reviews.clientWidth + 1
        );
      };
      const scheduleUpdate = () => {
        if (signal.aborted) return;
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(update);
      };
      const observer = new ResizeObserver(scheduleUpdate);
      observer.observe(reviews);
      signal.addEventListener('abort', () => {
        cancelAnimationFrame(frameId);
        observer.disconnect();
      }, { once: true });
      scheduleUpdate();

      if (document.fonts?.ready) {
        document.fonts.ready.then(scheduleUpdate);
      }
    };

    const initStickyTopReviewLabelFit = (topBarRoot, signal) => {
      const reviews = topBarRoot?.querySelector('.sb-product-main__sticky-reviews');
      const reviewCount = reviews?.querySelector('.sb-product-main__sticky-review-count');
      const fullLabel = reviewCount?.querySelector('.sb-product-main__sticky-review-count-full');
      const compactLabel = reviewCount?.querySelector('.sb-product-main__sticky-review-count-compact');
      const stickyBar = topBarRoot?.querySelector('.sb-product-main__sticky-bar');
      const segmentedControls = topBarRoot?.querySelector('[data-sticky-segmented-controls]');
      const addButton = topBarRoot?.querySelector('[data-sticky-add-button]');
      if (
        !topBarRoot
        || !reviews
        || !reviewCount
        || !fullLabel
        || !compactLabel
        || !stickyBar
        || !segmentedControls
        || stickyTopReviewLabelObserverMap.has(topBarRoot)
      ) return;

      const desktopViewport = window.matchMedia('(min-width: 990px)');
      let frameId = 0;
      const update = () => {
        frameId = 0;

        if (!desktopViewport.matches) {
          stickyBar.style.removeProperty('--sb-product-sticky-segmented-width');
          reviewCount.classList.remove('sb-product-main__sticky-review-count--compact');
          return;
        }

        const stickyBarStyle = getComputedStyle(stickyBar);
        const readPixelValue = (property, fallback) => {
          const value = Number.parseFloat(stickyBarStyle.getPropertyValue(property));
          return Number.isFinite(value) ? value : fallback;
        };
        const minimumClearance = readPixelValue('--sb-product-sticky-segmented-clearance', 20);
        const minimumWidth = readPixelValue('--sb-product-sticky-segmented-min-width', 360);
        const preferredWidth = readPixelValue('--sb-product-sticky-segmented-preferred-width', 500);

        stickyBar.style.setProperty('--sb-product-sticky-segmented-width', `${preferredWidth}px`);
        reviewCount.classList.remove('sb-product-main__sticky-review-count--compact');
        const preferredSegmentedRect = segmentedControls.getBoundingClientRect();
        const addButtonLeft = addButton?.getBoundingClientRect().left ?? Number.POSITIVE_INFINITY;

        const widthForClearance = (reviewLabelRight) => {
          const leftClearance = preferredSegmentedRect.left - reviewLabelRight;
          const rightClearance = addButtonLeft - preferredSegmentedRect.right;
          const requiredInset = Math.max(0, minimumClearance - leftClearance, minimumClearance - rightClearance);
          return Math.max(minimumWidth, preferredWidth - (requiredInset * 2));
        };
        const maintainsClearance = (width, reviewLabelRight) => {
          const edgeInset = (preferredWidth - width) / 2;
          const leftClearance = preferredSegmentedRect.left + edgeInset - reviewLabelRight;
          const rightClearance = addButtonLeft - (preferredSegmentedRect.right - edgeInset);
          return leftClearance >= minimumClearance - 0.01 && rightClearance >= minimumClearance - 0.01;
        };

        const fullLabelRight = fullLabel.getBoundingClientRect().right;
        const fullControlWidth = widthForClearance(fullLabelRight);
        if (maintainsClearance(fullControlWidth, fullLabelRight)) {
          stickyBar.style.setProperty('--sb-product-sticky-segmented-width', `${fullControlWidth}px`);
          return;
        }

        reviewCount.classList.add('sb-product-main__sticky-review-count--compact');
        const compactControlWidth = widthForClearance(compactLabel.getBoundingClientRect().right);
        stickyBar.style.setProperty('--sb-product-sticky-segmented-width', `${compactControlWidth}px`);
      };
      const scheduleUpdate = () => {
        if (signal.aborted) return;
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(update);
      };
      const observer = new ResizeObserver(scheduleUpdate);
      const syncObserverTargets = () => {
        observer.disconnect();
        if (desktopViewport.matches) {
          observer.observe(stickyBar);
          if (addButton) observer.observe(addButton);
        }
        scheduleUpdate();
      };
      desktopViewport.addEventListener('change', syncObserverTargets, { signal });
      stickyTopReviewLabelObserverMap.set(topBarRoot, observer);
      signal.addEventListener('abort', () => {
        cancelAnimationFrame(frameId);
        observer.disconnect();
        stickyTopReviewLabelObserverMap.delete(topBarRoot);
      }, { once: true });
      syncObserverTargets();

      if (document.fonts?.ready) {
        document.fonts.ready.then(scheduleUpdate);
      }
    };

    const initStickyPriceFit = (root, signal) => {
      const priceStack = root.querySelector('.sb-product-main__sticky-bottom-price-stack');
      const comparePrice = priceStack?.querySelector('.sb-product-main__sticky-bottom-compare-price');
      const currentPrice = priceStack?.querySelector('.sb-product-main__sticky-bottom-current-price');
      const bottomBar = root.querySelector('.sb-product-main__sticky-bottom-bar');
      const addButton = root.querySelector('[data-sticky-bottom-add-button]');
      if (!priceStack || !comparePrice || !currentPrice) return;

      let frameId = 0;
      const update = () => {
        frameId = 0;
        comparePrice.classList.remove('sb-product-main__sticky-bottom-compare-price--compact');
        if (comparePrice.hidden) return;

        const priceStackStyle = getComputedStyle(priceStack);
        const gap = Number.parseFloat(priceStackStyle.columnGap || priceStackStyle.gap || '0') || 0;
        const requiredWidth = comparePrice.scrollWidth + gap + currentPrice.scrollWidth;
        const priceStackRect = priceStack.getBoundingClientRect();
        const addButtonRect = addButton?.getBoundingClientRect();
        const visibleWidthBeforeButton = addButtonRect && addButtonRect.left > priceStackRect.left
          ? addButtonRect.left - priceStackRect.left
          : priceStack.clientWidth;
        comparePrice.classList.toggle(
          'sb-product-main__sticky-bottom-compare-price--compact',
          requiredWidth > visibleWidthBeforeButton + 1
        );
      };
      const scheduleUpdate = () => {
        if (signal.aborted) return;
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(update);
      };
      const resizeObserver = new ResizeObserver(scheduleUpdate);
      const mutationObserver = new MutationObserver(scheduleUpdate);
      resizeObserver.observe(priceStack);
      if (bottomBar) resizeObserver.observe(bottomBar);
      mutationObserver.observe(priceStack, {
        attributes: true,
        attributeFilter: ['hidden'],
        characterData: true,
        childList: true,
        subtree: true,
      });
      signal.addEventListener('abort', () => {
        cancelAnimationFrame(frameId);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      }, { once: true });
      scheduleUpdate();

      if (document.fonts?.ready) {
        document.fonts.ready.then(scheduleUpdate);
      }
    };

    const cleanupVideoStoryFullscreenTracking = (player) => {
      if (!(player instanceof HTMLVideoElement)) return;
      const cleanup = videoStoryFullscreenCleanupMap.get(player);
      if (typeof cleanup === 'function') cleanup();
      videoStoryFullscreenCleanupMap.delete(player);
    };

    const requestElementFullscreen = (element) => {
      if (!(element instanceof HTMLElement)) return false;

      try {
        if (typeof element.requestFullscreen === 'function') {
          const requestPromise = element.requestFullscreen();
          if (requestPromise && typeof requestPromise.catch === 'function') {
            requestPromise.catch(() => {});
          }
          return true;
        }

        if (typeof element.webkitRequestFullscreen === 'function') {
          element.webkitRequestFullscreen();
          return true;
        }
      } catch (_) {}

      return false;
    };

    const initProductRoot = (root, unloadingStickyBar = null) => {
      if (!root) return;
      let stickyTopBarRoot = root.closest('[data-quick-add-modal]')
        ? null : document.querySelector('.sb-product-top-sticky-accessory-bar');
      if (stickyTopBarRoot === unloadingStickyBar) stickyTopBarRoot = null;
      const previousLifetime = productLifetimes.get(root);
      if (previousLifetime?.stickyTopBarRoot === stickyTopBarRoot) return;
      previousLifetime?.controller.abort();
      const variantSelect = root.querySelector('[data-product-variant-id]');
      const quantityInput = root.querySelector('[data-product-quantity]');
      const variantsJsonElement = root.querySelector('[data-product-variants-json]');
      const mediaGrid = root.querySelector('[data-product-media-grid]');
      const inTheBoxContainer = root.querySelector('[data-in-the-box]');
      const inTheBoxGrid = root.querySelector('[data-in-the-box-grid]');
      const inTheBoxDivider = root.querySelector('[data-in-the-box-divider]');
      const variantExplainer = root.querySelector('[data-product-variant-explainer-body]');
      const defaultVariantExplainer = root.querySelector('[data-product-variant-explainer-default]');
      const variantExplainerTemplates = new Map(
        Array.from(root.querySelectorAll('[data-product-variant-explainer]')).map((template) => [
          template.dataset.productVariantExplainer,
          template,
        ])
      );
      const productDescriptionTrigger = root.querySelector('[data-product-description-trigger]');
      const productDescriptionItem = root.querySelector('[data-product-description-item]');
      const optionGroups = Array.from(root.querySelectorAll('[data-option-position]'));
      const selectorListItems = Array.from(root.querySelectorAll('[data-selector-list-item]'));
      const videoStoryTriggers = Array.from(root.querySelectorAll('[data-video-story-trigger]'));
      const videoStoryPlayer = root.querySelector('[data-video-story-player]');
      const selectorListOptionPosition = Number.parseInt(root.dataset.selectorListOptionPosition || '', 10);
      const selectorListOptionIndex =
        Number.isFinite(selectorListOptionPosition) && selectorListOptionPosition > 0
          ? selectorListOptionPosition - 1
          : -1;
      if (!variantSelect || !variantsJsonElement) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsJsonElement.textContent || '[]');
      } catch (error) {
        return;
      }
      if (!Array.isArray(variants) || !variants.length) return;

      const controller = new AbortController();
      const { signal } = controller;
      productLifetimes.set(root, { controller, stickyTopBarRoot });
      const listen = (target, type, callback, options = {}) => {
        target.addEventListener(type, callback, { signal, ...options });
      };
      const pendingFrames = new Set();
      const nextFrame = (callback) => {
        if (signal.aborted) return;
        const frame = window.requestAnimationFrame(() => {
          pendingFrames.delete(frame);
          if (!signal.aborted) callback();
        });
        pendingFrames.add(frame);
      };
      signal.addEventListener('abort', () => {
        pendingFrames.forEach((frame) => window.cancelAnimationFrame(frame));
        pendingFrames.clear();
        pricingRefreshers.delete(root);
        productLifetimes.delete(root);
      }, { once: true });
      const refreshPrices = [];
      pricingRefreshers.set(root, refreshPrices);

      const readOptionalProductData = (selector) => {
        const element = root.querySelector(selector);
        if (!element) return null;
        try {
          return JSON.parse(element.textContent || '{}');
        } catch (_) {
          return null;
        }
      };
      const variantMediaMap = readOptionalProductData('[data-product-variant-media-json]');
      const variantMediaCaptionsMap = readOptionalProductData('[data-product-variant-media-captions-json]');
      const variantStickyImageMap = readOptionalProductData('[data-product-variant-sticky-image-json]');
      const variantStickyHoverImageMap = readOptionalProductData('[data-product-variant-sticky-hover-image-json]');
      const variantInTheBoxMap = readOptionalProductData('[data-product-variant-in-the-box-json]');
      const addButton = root.querySelector('[data-primary-product-add-button]');
      const addButtonLabel = addButton ? addButton.querySelector('[data-add-button-label]') : null;
      const isQuickAddProductRoot = Boolean(root.closest('[data-quick-add-modal]'));
      const stickyImage = stickyTopBarRoot?.querySelector('[data-sticky-image]') || root.querySelector('[data-sticky-image]');
      const stickyBottomImage = root.querySelector('.sb-product-main__sticky-bottom-image');
      const stickyHoverImage = stickyTopBarRoot?.querySelector('[data-sticky-hover-image]') || root.querySelector('[data-sticky-hover-image]');
      const stickyBottomHoverImage = root.querySelector('[data-sticky-bottom-hover-image]');
      const stickyComparePriceElement = stickyTopBarRoot?.querySelector('[data-sticky-compare-price]') || root.querySelector('[data-sticky-compare-price]');
      const stickyCurrentPriceElement = stickyTopBarRoot?.querySelector('[data-sticky-current-price]') || root.querySelector('[data-sticky-current-price]');
      const stickyAddButton = stickyTopBarRoot?.querySelector('[data-sticky-add-button]') || root.querySelector('[data-sticky-add-button]');
      const stickyAddButtonLabel = stickyAddButton ? stickyAddButton.querySelector('[data-sticky-add-button-label]') : null;
      const stickyBottomBarWrap = root.querySelector('.sb-product-main__sticky-bottom-bar-wrap');
      const stickyBottomComparePriceElement = root.querySelector('[data-sticky-bottom-compare-price]');
      const stickyBottomCurrentPriceElement = root.querySelector('[data-sticky-bottom-current-price]');
      const stickyBottomAddButton = root.querySelector('[data-sticky-bottom-add-button]');
      const stickyBottomAddButtonLabel = stickyBottomAddButton
        ? stickyBottomAddButton.querySelector('[data-sticky-bottom-add-button-label]')
        : null;
      initStickyReviewLabelFit(root, signal);
      initStickyTopReviewLabelFit(stickyTopBarRoot, signal);
      initStickyPriceFit(root, signal);
      const stickySegmentedControls = stickyTopBarRoot?.querySelector('[data-sticky-segmented-controls]') || root.querySelector('[data-sticky-segmented-controls]');
      const stickySegmentIndicator = stickySegmentedControls
        ? stickySegmentedControls.querySelector('.sb-product-main__sticky-segment-indicator')
        : null;
      const stickySegmentButtons = stickySegmentedControls
        ? Array.from(stickySegmentedControls.querySelectorAll('[data-sticky-segment-option]'))
        : Array.from(root.querySelectorAll('[data-sticky-segment-option]'));
      const hasPageHeader = !root.classList.contains('sb-product-main--no-page-header');
      const isMobileProductViewport = window.matchMedia('(max-width: 989px)');
      const priceRow = root.querySelector('[data-product-price-row]');
      const comparePriceElement = root.querySelector('[data-product-compare-price]');
      const currentPriceElement = root.querySelector('[data-product-current-price]');
      const savingsBadgeElement = root.querySelector('[data-product-savings-badge]');
      const monthlyPriceElement = root.querySelector('[data-product-monthly-price]');
      const priceLiveRegion = root.querySelector('[data-product-price-live-region]');
      const productTitle = root.dataset.productTitle || '';
      const productId = root.dataset.productId || '';
      const hideDiscounts = root.dataset.hideDiscounts === 'true';
      const productMainConfig = readProductMainConfig(root);
      const requestedVariantId = isQuickAddProductRoot ? '' : new URL(window.location.href).searchParams.get('variant');
      const requestedVariant = variants.find((variant) => `${variant.id}` === requestedVariantId && variant.available);
      const selectedById = requestedVariant || variants.find((variant) => `${variant.id}` === `${variantSelect.value}`);
      let selectedOptions = Array.isArray(selectedById?.options) ? [...selectedById.options] : [];
      if (!selectedOptions.length && Array.isArray(variants[0]?.options)) {
        selectedOptions = [...variants[0].options];
      }
      let currentVariantId = selectedById ? `${selectedById.id}` : `${variantSelect.value}`;
      let hasVariantInitialized = false;

      if (stickyAddButton && addButton) {
        listen(stickyAddButton, 'click', () => {
          if (addButton.disabled) return;
          if (window.SBCart && typeof window.SBCart.setNextAddSourceElement === 'function') {
            window.SBCart.setNextAddSourceElement(stickyAddButton);
          }
          addButton.click();
        });
      }

      if (stickyBottomAddButton && addButton) {
        listen(stickyBottomAddButton, 'click', () => {
          if (addButton.disabled) return;
          if (window.SBCart && typeof window.SBCart.setNextAddSourceElement === 'function') {
            window.SBCart.setNextAddSourceElement(stickyBottomAddButton);
          }
          addButton.click();
        });
      }

      if (productDescriptionTrigger && productDescriptionItem) {
        listen(productDescriptionTrigger, 'click', () => {
          const isOpening = !productDescriptionItem.classList.contains('is-open');
          productDescriptionItem.classList.toggle('is-open', isOpening);
          productDescriptionTrigger.setAttribute('aria-expanded', isOpening ? 'true' : 'false');
        });
      }

      const escapeHtml = (value) =>
        String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const currencyCode = `${priceRow?.dataset.currency || root.dataset.productCurrency || ''}`.trim().toUpperCase();
      const storefrontPricing = window.SBStorefrontPricing || null;
      const savePercentTemplate = productMainConfig.savePercentTemplate || '__PERCENT__';
      const priceLabel = productMainConfig.priceLabel || '';
      const regularPriceLabel = productMainConfig.regularPriceLabel || '';
      const salePriceLabel = productMainConfig.salePriceLabel || '';
      const priceAvailableLabel = productMainConfig.priceAvailableLabel || '';
      const priceAvailabilityTemplate = productMainConfig.priceAvailabilityTemplate || '__PRICE__ __AVAILABILITY__';
      const salePriceAvailabilityTemplate =
        productMainConfig.salePriceAvailabilityTemplate || '__SALE_PRICE__ __REGULAR_PRICE__ __AVAILABILITY__';
      const addToCartLabel = productMainConfig.addToCartLabel || '';
      const soldOutLabel = productMainConfig.soldOutLabel || '';
      const addLabel = productMainConfig.addLabel || '';
      const setPriceElement = (element, label, value) => {
        if (!element) return;
        const labelElement = element.querySelector('[data-product-price-accessibility-label]');
        const valueElement = element.querySelector('[data-product-price-value]');
        if (labelElement && valueElement) {
          labelElement.textContent = label ? `${label} ` : '';
          valueElement.textContent = value;
          return;
        }
        element.textContent = value;
      };
      const updatePriceLiveRegion = ({ resolvedPricing, hasComparePrice, available, formatter = formatMoney } = {}) => {
        if (!priceLiveRegion || !resolvedPricing) return;
        const availability = available ? priceAvailableLabel : soldOutLabel;
        const currentPrice = formatter(resolvedPricing.currentPriceCents);
        const nextAnnouncement = hasComparePrice
          ? salePriceAvailabilityTemplate
              .replace('__SALE_PRICE_LABEL__', salePriceLabel)
              .replace('__SALE_PRICE__', currentPrice)
              .replace('__REGULAR_PRICE_LABEL__', regularPriceLabel)
              .replace('__REGULAR_PRICE__', formatter(resolvedPricing.comparePriceCents))
              .replace('__AVAILABILITY__', availability)
          : priceAvailabilityTemplate
              .replace('__PRICE_LABEL__', priceLabel)
              .replace('__PRICE__', currentPrice)
              .replace('__AVAILABILITY__', availability);
        if (priceLiveRegion.textContent !== nextAnnouncement) {
          priceLiveRegion.textContent = nextAnnouncement;
        }
      };
      const resolveFreeShippingThresholdDisplay = () => {
        const fallbackDisplay = `${root.dataset.freeShippingThresholdDisplay || ''}`;
        const thresholdCents = Number.parseInt(`${root.dataset.freeShippingThresholdCents || ''}`, 10);
        const thresholdCurrencyCode = `${root.dataset.productCurrency || currencyCode || ''}`.trim().toUpperCase();
        if (!Number.isFinite(thresholdCents) || thresholdCents <= 0) return fallbackDisplay;

        if (storefrontPricing && typeof storefrontPricing.formatMoney === 'function') {
          const formatted = storefrontPricing.formatMoney(thresholdCents, thresholdCurrencyCode, { context: 'storefront' });
          if (`${formatted || ''}`.trim() !== '') {
            root.dataset.freeShippingThresholdDisplay = formatted;
            return formatted;
          }
        }

        return fallbackDisplay;
      };

      const syncFreeShippingThresholdCopy = () => {
        const currentThresholdDisplay = `${root.dataset.freeShippingThresholdDisplay || ''}`;
        const formattedThresholdDisplay = resolveFreeShippingThresholdDisplay();
        if (!currentThresholdDisplay || !formattedThresholdDisplay || currentThresholdDisplay === formattedThresholdDisplay) return;

        root.querySelectorAll('.sb-product-main__purchase-guarantee-text').forEach((textElement) => {
          if (!(textElement instanceof HTMLElement)) return;
          const currentText = `${textElement.textContent || ''}`;
          if (!currentText.includes(currentThresholdDisplay)) return;
          textElement.textContent = currentText.split(currentThresholdDisplay).join(formattedThresholdDisplay);
        });
      };

      syncFreeShippingThresholdCopy();

      const syncVariantUrl = (variantId) => {
        if (!variantId || isQuickAddProductRoot) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', `${variantId}`);
        if (url.toString() !== window.location.href) {
          window.history.replaceState({}, '', url.toString());
        }
      };

      const normalizeSegment = (value) => (value === 'buy-now' ? 'buy-now' : 'learn-more');
      const segmentScrollMemory = {
        'learn-more': window.scrollY || window.pageYOffset || 0,
        'buy-now': null,
      };
      const clampScrollTop = (value) => {
        const nextValue = Number.isFinite(value) ? value : 0;
        const maxScroll = Math.max(
          0,
          (document.scrollingElement?.scrollHeight || document.documentElement.scrollHeight || 0) - window.innerHeight
        );
        return Math.min(Math.max(nextValue, 0), maxScroll);
      };

      const getStickyRevealThreshold = () => {
        const value = Number.parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--sb-top-stack-bottom')
        );
        return Number.isFinite(value) ? value : 0;
      };

      const captureSegmentScroll = (segmentValue = root.dataset.pageSegment || 'learn-more') => {
        const activeSegment = normalizeSegment(segmentValue);
        segmentScrollMemory[activeSegment] = window.scrollY || window.pageYOffset || 0;
      };
      const headerComponent = document.querySelector('#header-component');

      let ownsHeaderScrollSuspension = false;
      const setHeaderScrollSyncSuspended = (suspended) => {
        ownsHeaderScrollSuspension = suspended;
        if (window.SBHeaderNav && typeof window.SBHeaderNav.setScrollSyncSuspended === 'function') {
          window.SBHeaderNav.setScrollSyncSuspended(suspended);
        }
      };

      const refreshHeaderStickyMode = () => {
        if (window.SBHeaderNav && typeof window.SBHeaderNav.refreshScrollMode === 'function') {
          window.SBHeaderNav.refreshScrollMode();
        }
      };

      const captureHeaderVisualState = () => {
        if (window.SBHeaderNav && typeof window.SBHeaderNav.captureVisualState === 'function') {
          return window.SBHeaderNav.captureVisualState();
        }
        return null;
      };

      const restoreHeaderVisualState = (snapshot) => {
        if (window.SBHeaderNav && typeof window.SBHeaderNav.restoreVisualState === 'function') {
          window.SBHeaderNav.restoreVisualState(snapshot);
        }
      };

      const getHeaderHideOffset = () => {
        const rootStyles = getComputedStyle(document.documentElement);
        const measuredHeaderHeight = headerComponent instanceof HTMLElement ? headerComponent.offsetHeight : 0;
        const fallbackHeaderHeight = Number.parseFloat(rootStyles.getPropertyValue('--sb-header-height'));
        const announcementHeight = Number.parseFloat(rootStyles.getPropertyValue('--sb-announcement-height'));
        const headerHeight = measuredHeaderHeight || (Number.isFinite(fallbackHeaderHeight) ? fallbackHeaderHeight : 0);
        return Math.ceil(Math.max(
          0,
          headerHeight + (Number.isFinite(announcementHeight) ? announcementHeight : 0)
        ));
      };

      const syncStickySegmentIndicator = (segmentValue, { immediate = false } = {}) => {
        if (!stickySegmentedControls || !stickySegmentIndicator || !stickySegmentButtons.length) return;
        const activeSegment = normalizeSegment(segmentValue);
        const activeButton = stickySegmentButtons.find(
          (button) => button.dataset.stickySegmentOption === activeSegment
        ) || stickySegmentButtons[0];
        if (!activeButton) return;

        const activeButtonIndex = stickySegmentButtons.indexOf(activeButton);
        const nextTransform = activeButtonIndex > 0
          ? 'translate3d(calc(100% + var(--sb-segment-gap)), 0, 0)'
          : 'translate3d(0, 0, 0)';

        stickySegmentIndicator.style.removeProperty('left');
        stickySegmentIndicator.style.removeProperty('top');
        stickySegmentIndicator.style.removeProperty('width');
        stickySegmentIndicator.style.removeProperty('height');

        if (immediate) {
          const currentTransition = stickySegmentIndicator.style.transition;
          stickySegmentIndicator.style.transition = 'none';
          stickySegmentIndicator.style.transform = nextTransform;
          stickySegmentIndicator.offsetHeight;
          stickySegmentIndicator.style.transition = currentTransition;
          return;
        }

        stickySegmentIndicator.style.transform = nextTransform;
      };

      const getSegmentManagedSections = (currentProductSection) => {
        if (isQuickAddProductRoot) return [];
        const mainContent = document.querySelector('#MainContent');
        const allSections = Array.from(
          (mainContent || document).querySelectorAll('.shopify-section[id*="template--"]')
        );
        const mainSections = allSections.filter((section) => !section.querySelector('[data-sb-product-top-sticky-accessory-bar]'));
        if (currentProductSection && mainSections.includes(currentProductSection)) return mainSections;
        return Array.from(document.querySelectorAll('.shopify-section[id*="template--"]')).filter(
          (section) => !section.querySelector('[data-sb-product-top-sticky-accessory-bar]')
        );
      };

      const applyPageSegmentVisibility = (segmentValue) => {
        const activeSegment = normalizeSegment(segmentValue);
        const productSection = root.closest('.shopify-section[id*="template--"]');
        root.classList.toggle('is-segment-learn-more', activeSegment === 'learn-more');
        if (isQuickAddProductRoot) return;
        if (!productSection) return;

        const orderedSections = getSegmentManagedSections(productSection);
        const productSectionIndex = orderedSections.indexOf(productSection);
        if (productSectionIndex < 0) return;

        const newlyVisibleSections = [productSection];
        orderedSections.forEach((section, index) => {
          if (section === productSection) return;

          const shouldHide = activeSegment === 'buy-now'
            ? index < productSectionIndex
            : index > productSectionIndex;

          if (section.classList.contains('sb-segment-hidden') === shouldHide) return;
          if (!shouldHide) newlyVisibleSections.push(section);
          section.classList.toggle('sb-segment-hidden', shouldHide);
          if (shouldHide) {
            section.setAttribute('aria-hidden', 'true');
          } else {
            section.removeAttribute('aria-hidden');
          }
        });
        return newlyVisibleSections;
      };

      const applyStickySegment = (
        segmentValue,
        {
          immediateIndicator = false,
          syncUrl = false,
          restoreScroll = false,
        } = {}
      ) => {
        const activeSegment = normalizeSegment(segmentValue);
        const previousSegment = normalizeSegment(root.dataset.pageSegment || 'learn-more');
        const segmentChanged = previousSegment !== activeSegment;
        if (previousSegment !== activeSegment) {
          captureSegmentScroll(previousSegment);
        }
        root.dataset.pageSegment = activeSegment;
        if (!isQuickAddProductRoot) {
          document.documentElement.setAttribute('data-sb-page-segment', activeSegment);
        }

        stickySegmentButtons.forEach((button) => {
          const isActive = button.dataset.stickySegmentOption === activeSegment;
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('weight-semibold', isActive);
          button.classList.toggle('weight-medium', !isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        syncStickySegmentIndicator(activeSegment, { immediate: immediateIndicator });

        let visibleSections = [];
        try {
          visibleSections = applyPageSegmentVisibility(activeSegment) || [];
        } catch (error) {
          // Keep segmented controls usable even if section visibility fails.
        }
        nextFrame(() => {
          visibleSections.forEach(section => window.SBSectionMotion?.initScope(section));
        });
        syncStickyBottomBarVisibility();

        if (!isQuickAddProductRoot && restoreScroll && segmentChanged) {
          const headerVisualState = captureHeaderVisualState();
          const savedScroll = segmentScrollMemory[activeSegment];
          const savedScrollTop = clampScrollTop(Number.isFinite(savedScroll) ? savedScroll : 0);
          const shouldPreserveHiddenHeader = headerVisualState?.stickyMode === 'scroll-up'
            && headerVisualState?.stickyState === 'idle';
          const nextScrollTop = shouldPreserveHiddenHeader
            ? Math.max(savedScrollTop, getHeaderHideOffset())
            : savedScrollTop;
          setHeaderScrollSyncSuspended(true);
          window.scrollTo({
            top: nextScrollTop,
            behavior: 'auto',
          });
          nextFrame(() => {
            setHeaderScrollSyncSuspended(false);
            refreshHeaderStickyMode();
            if (headerVisualState?.stickyMode === 'scroll-up') {
              restoreHeaderVisualState(headerVisualState);
            }
          });
        } else {
          captureSegmentScroll(activeSegment);
        }

        if (!syncUrl || isQuickAddProductRoot) return;
        const url = new URL(window.location.href);
        if (activeSegment === 'buy-now') {
          url.searchParams.set('p', 'buy-now');
        } else {
          url.searchParams.delete('p');
        }
        window.history.replaceState({}, '', url.toString());
      };

      const syncSegmentFromUrl = ({ immediateIndicator = false } = {}) => {
        const segmentFromUrl = normalizeSegment(new URL(window.location.href).searchParams.get('p') || '');
        applyStickySegment(segmentFromUrl, { immediateIndicator });
      };

      const syncStickyBottomBarVisibility = () => {
        const isMobileViewport = isMobileProductViewport.matches;
        const activeSegment = normalizeSegment(root.dataset.pageSegment || 'learn-more');
        // Only the Buy now tab needs to wait for the in-page purchase button.
        // Plain products and Learn more share the immediately available dock.
        let shouldShowBottomBar = isMobileViewport;
        if (isMobileViewport && hasPageHeader && activeSegment === 'buy-now') {
          const revealThreshold = getStickyRevealThreshold();
          const addButtonRect = addButton?.getBoundingClientRect?.() || null;
          shouldShowBottomBar = Boolean(addButtonRect) && addButtonRect.bottom <= revealThreshold;
        }
        stickyBottomBarWrap?.classList.toggle('sb-product-main__sticky-bottom-bar-wrap--hidden', !shouldShowBottomBar);
        if (stickyBottomBarWrap) stickyBottomBarWrap.inert = !shouldShowBottomBar;
      };

      if (stickySegmentedControls && stickySegmentButtons.length) {
        syncSegmentFromUrl({ immediateIndicator: true });
        let segmentArrivalController = null;
        let segmentArrivalTimer = 0;

        const clearSegmentArrival = () => {
          segmentArrivalController?.abort();
          segmentArrivalController = null;
          window.clearTimeout(segmentArrivalTimer);
          segmentArrivalTimer = 0;
          stickySegmentedControls.classList.remove('is-arriving-buy-now', 'is-arriving-learn-more');
        };

        signal.addEventListener('abort', clearSegmentArrival, { once: true });

        const animateSegmentArrival = (segmentValue) => {
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

          const activeSegment = normalizeSegment(segmentValue);
          const arrivalClass = `is-arriving-${activeSegment}`;
          const centerFacingHalf = stickySegmentedControls.querySelector(
            `[data-sticky-segment-indicator-half="${activeSegment === 'buy-now' ? 'start' : 'end'}"]`
          );
          if (!centerFacingHalf || !stickySegmentIndicator) return;

          clearSegmentArrival();
          const controlsStyle = window.getComputedStyle(stickySegmentedControls);
          const parseCssTime = (value, fallback) => {
            const normalized = String(value || '').trim();
            const amount = Number.parseFloat(normalized);
            if (!Number.isFinite(amount)) return fallback;
            return normalized.endsWith('ms') ? amount : amount * 1000;
          };
          const edgeDuration = parseCssTime(controlsStyle.getPropertyValue('--sb-segment-edge-duration'), 260);
          const slideDuration = parseCssTime(controlsStyle.getPropertyValue('--sb-segment-slide-duration'), 220);
          segmentArrivalController = new AbortController();
          const { signal } = segmentArrivalController;

          const expandEdge = () => {
            if (signal.aborted || !stickySegmentedControls.classList.contains(arrivalClass)) return;
            window.clearTimeout(segmentArrivalTimer);
            listen(centerFacingHalf, 'transitionend', (event) => {
              if (event.propertyName === 'transform') clearSegmentArrival();
            }, { once: true, signal });
            stickySegmentedControls.classList.remove(arrivalClass);
            segmentArrivalTimer = window.setTimeout(clearSegmentArrival, edgeDuration + 50);
          };

          stickySegmentedControls.classList.add(arrivalClass);
          listen(stickySegmentIndicator, 'transitionend', (event) => {
            if (event.propertyName === 'transform') expandEdge();
          }, { once: true, signal });
          segmentArrivalTimer = window.setTimeout(expandEdge, slideDuration + 50);
        };

        const syncInitialSegmentVisibility = () => {
          syncSegmentFromUrl({ immediateIndicator: true });
        };

        if (document.readyState === 'loading') {
          listen(document, 'DOMContentLoaded', syncInitialSegmentVisibility, { once: true });
        } else {
          nextFrame(syncInitialSegmentVisibility);
        }

        stickySegmentButtons.forEach((button) => {
          listen(button, 'click', (event) => {
            const nextSegment = normalizeSegment(button.dataset.stickySegmentOption || 'learn-more');
            const segmentChanged = normalizeSegment(root.dataset.pageSegment || 'learn-more') !== nextSegment;
            applyStickySegment(nextSegment, {
              immediateIndicator: event.detail === 0,
              syncUrl: true,
              restoreScroll: true,
            });
            if (segmentChanged && event.detail !== 0) {
              animateSegmentArrival(nextSegment);
            }
          });
        });

        listen(window, 'popstate', () => {
          /* Hash-only navigation (e.g. #reviews anchors) also fires popstate;
             it must not reset the segment. #reviews implies buy-now. */
          if (window.location.hash === '#reviews') {
            applyStickySegment('buy-now', { syncUrl: true });
            return;
          }
          const nextSegment = normalizeSegment(new URL(window.location.href).searchParams.get('p') || '');
          applyStickySegment(nextSegment, { restoreScroll: true });
        });
      }

      const dispatchVariantSelectedEvent = (variant, sourceId = 'product-main') => {
        if (!variant?.id) return;
        root.dispatchEvent(
          new CustomEvent(ThemeEvents.variantSelected, {
            bubbles: true,
            detail: {
              resource: { id: `${variant.id}` },
              sourceId,
            },
          })
        );
      };

      const dispatchVariantUpdateEvent = (variant, sourceId = 'product-main') => {
        if (!variant?.id) return;
        root.dispatchEvent(
          new CustomEvent(ThemeEvents.variantUpdate, {
            bubbles: true,
            detail: {
              resource: variant,
              sourceId,
              data: {
                productId,
              },
            },
          })
        );
      };

      const stopVideoStoryPlayback = () => {
        if (!(videoStoryPlayer instanceof HTMLVideoElement)) return;
        cleanupVideoStoryFullscreenTracking(videoStoryPlayer);
        videoStoryPlayer.pause();
        videoStoryPlayer.controls = false;
        videoStoryPlayer.removeAttribute('controls');
        videoStoryPlayer.playsInline = true;
        videoStoryPlayer.setAttribute('playsinline', '');
        videoStoryPlayer.setAttribute('webkit-playsinline', 'true');
        videoStoryPlayer.hidden = true;
        videoStoryPlayer.removeAttribute('src');
        videoStoryPlayer.load();
      };

      signal.addEventListener('abort', () => {
        [stickyHoverImage, stickyBottomHoverImage].forEach((image) => {
          if (image) { image.onload = null; image.onerror = null; }
        });
        cleanupVideoStoryFullscreenTracking(videoStoryPlayer);
        if (videoStoryPlayer?.getAttribute('src')) stopVideoStoryPlayback();
        if (ownsHeaderScrollSuspension) setHeaderScrollSyncSuspended(false);
        root.querySelectorAll('video').forEach((video) => {
          window.SBMediaLoader?.video.unregister?.(video);
          video.pause();
          delete video.dataset.galleryVideoInit;
        });
      }, { once: true });

      const trackVideoStoryFullscreenExit = () => {
        if (!(videoStoryPlayer instanceof HTMLVideoElement)) return;

        cleanupVideoStoryFullscreenTracking(videoStoryPlayer);

        const onDocumentFullscreenChange = () => {
          const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
          if (fullscreenElement === videoStoryPlayer) return;
          stopVideoStoryPlayback();
        };

        const onVideoFullscreenExit = () => {
          stopVideoStoryPlayback();
        };

        listen(document, 'fullscreenchange', onDocumentFullscreenChange);
        listen(document, 'webkitfullscreenchange', onDocumentFullscreenChange);
        listen(videoStoryPlayer, 'webkitendfullscreen', onVideoFullscreenExit);

        videoStoryFullscreenCleanupMap.set(videoStoryPlayer, () => {
          document.removeEventListener('fullscreenchange', onDocumentFullscreenChange);
          document.removeEventListener('webkitfullscreenchange', onDocumentFullscreenChange);
          videoStoryPlayer.removeEventListener('webkitendfullscreen', onVideoFullscreenExit);
        });
      };

      if (videoStoryPlayer) {
        listen(videoStoryPlayer, 'ended', stopVideoStoryPlayback);
      }

      if (videoStoryPlayer && videoStoryTriggers.length) {
        videoStoryTriggers.forEach((trigger) => {
          listen(trigger, 'click', () => {
            const videoUrl = (trigger.dataset.videoStoryUrl || '').trim();
            if (!videoUrl) return;
            stopVideoStoryPlayback();
            videoStoryPlayer.setAttribute('src', videoUrl);
            videoStoryPlayer.load();
            videoStoryPlayer.currentTime = 0;
            videoStoryPlayer.defaultMuted = false;
            videoStoryPlayer.muted = false;
            videoStoryPlayer.loop = false;
            videoStoryPlayer.controls = true;
            videoStoryPlayer.setAttribute('controls', 'controls');
            videoStoryPlayer.playsInline = false;
            videoStoryPlayer.removeAttribute('playsinline');
            videoStoryPlayer.removeAttribute('webkit-playsinline');
            videoStoryPlayer.hidden = false;
            const playPromise = videoStoryPlayer.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
            trackVideoStoryFullscreenExit();
            if (typeof videoStoryPlayer.webkitEnterFullscreen === 'function') {
              try {
                videoStoryPlayer.webkitEnterFullscreen();
              } catch (_) {
                requestElementFullscreen(videoStoryPlayer);
              }
            } else {
              requestElementFullscreen(videoStoryPlayer);
            }
          });
        });
      }

      const formatMoney = (centsValue) => {
        const amount = Number(centsValue) || 0;
        if (storefrontPricing && typeof storefrontPricing.formatMoney === 'function') {
          return storefrontPricing.formatMoney(amount, currencyCode, { context: 'storefront' });
        }
        return `${Math.round(amount / 100)}`;
      };
      const formatMonthlyMoney = (centsValue) => {
        const amount = Number(centsValue) || 0;
        const monthlyCents = Math.round(amount / 4);
        return storefrontPricing && typeof storefrontPricing.formatMoney === 'function'
          ? storefrontPricing.formatMoney(monthlyCents, currencyCode, { context: 'storefront' })
          : `${Math.round(monthlyCents / 100)}`;
      };

      const resolveDisplayPrice = (purchaseData, overrideProductId = productId, quantity = 1) => {
        const basePriceCents = Number(purchaseData?.price) || 0;
        const baseComparePriceCents = Number(purchaseData?.compare_at_price) || 0;
        if (hideDiscounts) {
          return {
            currentPriceCents: basePriceCents,
            comparePriceCents: null,
          };
        }

        if (storefrontPricing && typeof storefrontPricing.resolvePrice === 'function') {
          const resolved = storefrontPricing.resolvePrice({
            productId: overrideProductId,
            priceCents: basePriceCents,
            comparePriceCents: baseComparePriceCents,
            quantity,
          }) || {};
          const currentPriceCents = Number(resolved.currentPriceCents);
          const comparePriceCents = Number(resolved.comparePriceCents);
          return {
            currentPriceCents: Number.isFinite(currentPriceCents) ? currentPriceCents : basePriceCents,
            comparePriceCents:
              Number.isFinite(comparePriceCents) && comparePriceCents > (Number.isFinite(currentPriceCents) ? currentPriceCents : basePriceCents)
                ? comparePriceCents
                : null,
          };
        }

        if (baseComparePriceCents > basePriceCents) {
          return {
            currentPriceCents: basePriceCents,
            comparePriceCents: baseComparePriceCents,
          };
        }

        return {
          currentPriceCents: basePriceCents,
          comparePriceCents: null,
        };
      };

      const getStickyImageFromVariant = (variant) => {
        if (!variant) return '';

        const stickyImageSource = String(variantStickyImageMap?.[`${variant.id}`] || '').trim();
        if (stickyImageSource) return stickyImageSource;

        const featuredImageSource = variant?.featured_image?.src || variant?.featured_image?.url || '';
        if (featuredImageSource) return featuredImageSource;

        const tokens = variantMediaMap?.[`${variant.id}`];
        if (!Array.isArray(tokens)) return '';

        for (const token of tokens) {
          if (typeof token !== 'string') continue;
          const parts = token.split('::');
          const type = parts[0] || '';
          const source = parts[1] || '';
          const poster = parts[2] || '';
          if (type === 'image' && source) return source;
          if (type === 'video' && poster) return poster;
        }
        return '';
      };

      const getStickyImageSourceByVariantId = (variantId) => {
        const normalizedVariantId = `${variantId || ''}`;
        if (!normalizedVariantId) return '';

        const variant = variants.find((entry) => `${entry.id}` === normalizedVariantId);
        return getStickyImageFromVariant(variant);
      };

      const stickyImagePreloadCache = new Set();
      const preloadStickyImageSource = (source) => {
        const normalizedSource = String(source || '').trim();
        if (!normalizedSource || stickyImagePreloadCache.has(normalizedSource)) return;
        stickyImagePreloadCache.add(normalizedSource);
        const image = new Image();
        image.decoding = 'async';
        image.src = normalizedSource;
      };

      const preloadStickyImageSources = () => {
        variants.forEach((variant) => {
          preloadStickyImageSource(getStickyImageFromVariant(variant));
        });
      };

      const applyStickyImageSource = (source) => {
        const nextSource = String(source || '').trim();
        if (!nextSource) return;
        preloadStickyImageSource(nextSource);
        [stickyImage, stickyBottomImage].forEach((imageElement) => {
          if (!imageElement) return;
          if (imageElement.tagName === 'IMG') {
            if (imageElement.src !== nextSource) imageElement.src = nextSource;
            return;
          }
          imageElement.style.backgroundImage = `url("${nextSource}")`;
          imageElement.style.backgroundPosition = 'center';
          imageElement.style.backgroundSize = 'cover';
        });
      };

      const applyStickyHoverImageSource = (variantId) => {
        const source = String(variantStickyHoverImageMap?.[`${variantId || ''}`] || '').trim();
        [stickyHoverImage, stickyBottomHoverImage].forEach((imageElement) => {
          if (!(imageElement instanceof HTMLImageElement)) return;
          const imageWrap = imageElement.parentElement;
          if (!(imageWrap instanceof HTMLElement)) return;

          imageWrap.classList.remove('is-hover-image-ready');
          imageWrap.classList.toggle(
            imageElement === stickyHoverImage
              ? 'sb-product-main__sticky-image-wrap--has-hover'
              : 'sb-product-main__sticky-bottom-image-wrap--has-hover',
            Boolean(source)
          );

          if (!source) {
            imageElement.hidden = true;
            imageElement.removeAttribute('src');
            return;
          }

          imageElement.hidden = false;
          imageElement.dataset.stickyHoverSource = source;
          const markReady = async () => {
            if (signal.aborted || imageElement.dataset.stickyHoverSource !== source || !imageElement.complete || imageElement.naturalWidth <= 0) return;
            try {
              await imageElement.decode();
            } catch (_) {}
            if (!signal.aborted && imageElement.dataset.stickyHoverSource === source && imageElement.naturalWidth > 0) {
              imageWrap.classList.add('is-hover-image-ready');
            }
          };
          imageElement.onload = () => void markReady();
          imageElement.onerror = () => imageWrap.classList.remove('is-hover-image-ready');
          if (imageElement.getAttribute('src') !== source) imageElement.src = source;
          void markReady();
        });
      };

      if (stickyImage && stickyImage.tagName === 'IMG') {
        preloadStickyImageSource(stickyImage.currentSrc || stickyImage.getAttribute('src') || '');
      }
      if (stickyBottomImage && stickyBottomImage.tagName === 'IMG') {
        preloadStickyImageSource(stickyBottomImage.currentSrc || stickyBottomImage.getAttribute('src') || '');
      }
      const stickyWarmup = typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(preloadStickyImageSources, { timeout: 500 })
        : window.setTimeout(preloadStickyImageSources, 0);
      signal.addEventListener('abort', () => {
        if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(stickyWarmup);
        else window.clearTimeout(stickyWarmup);
      }, { once: true });
      applyStickyHoverImageSource(currentVariantId);

      function syncStickyBarMetrics({ immediateIndicator = true } = {}) {
        syncStickySegmentIndicator(root.dataset.pageSegment || 'learn-more', { immediate: immediateIndicator });
      }

      if (!isQuickAddProductRoot) {
        listen(document, 'sb:scroll-target:prepare', (event) => {
          if (event.detail?.target?.id !== 'reviews') return;
          const hasSegmentedControls = Boolean(stickySegmentedControls && stickySegmentButtons.length);
          if (hasSegmentedControls && normalizeSegment(root.dataset.pageSegment || 'learn-more') !== 'buy-now') {
            applyStickySegment('buy-now', { syncUrl: true });
          }
          syncStickyBarMetrics();
        });
      }

      syncStickyBarMetrics({ immediateIndicator: true });
      syncStickyBottomBarVisibility();
      captureSegmentScroll();

      if (!isQuickAddProductRoot) {
        listen(window,
          'scroll',
          () => {
            captureSegmentScroll();
            syncStickyBottomBarVisibility();
          },
          { passive: true }
        );
        listen(window, 'resize', () => {
          syncStickyBarMetrics({ immediateIndicator: true });
          syncStickyBottomBarVisibility();
        });

        listen(document, 'sb:cart-drawer:state-change', () => {
          syncStickyBarMetrics({ immediateIndicator: true });
        });
      }

      // The shared media loader (sb-media-loader.js) owns the whole
      // autoplay-loop lifecycle for these gallery renders: template-adoption
      // safety for the variant-switch rebuild (Safari brings template-born
      // elements back from a background tab with a dead decoder), decoder
      // release/restore while offscreen, resume on tab return, and the rVFC
      // dead-decoder watchdog. The hard-won Safari findings behind each of
      // those live as comments in the loader. This section only maps the
      // lifecycle onto its own reveal class, `is-video-ready`, which is what
      // hides the poster over the video's always-white background.
      const initGalleryVideos = () => {
        if (!mediaGrid) return;
        const lifecycle = window.SBMediaLoader?.video;
        mediaGrid.querySelectorAll('video.sb-product-main__media-asset--video').forEach((video) => {
          if (!lifecycle) {
            if (video.dataset.galleryVideoInit === 'true') return;
            video.dataset.galleryVideoInit = 'true';
            video.muted = true;
            video.autoplay = true;
            listen(video, 'playing', () => {
              video.closest('.sb-product-main__media-item--video')?.classList.add('is-video-ready');
            });
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {});
            }
            return;
          }
          lifecycle.register(video, {
            adopt: true,
            offscreen: 'release',
            hooks: {
              reveal: (target) => {
                target.closest('.sb-product-main__media-item--video')?.classList.add('is-video-ready');
              },
              conceal: (target) => {
                target.closest('.sb-product-main__media-item--video')?.classList.remove('is-video-ready');
              },
            },
          });
        });
      };

      const getMediaCaptionByPosition = (captionsByPosition, position) => {
        if (!captionsByPosition || typeof captionsByPosition !== 'object') return '';
        const rawCaption = captionsByPosition[`${position}`];
        if (typeof rawCaption !== 'string') return '';
        return rawCaption.trim();
      };

      const serializeMediaGridState = () => {
        if (!mediaGrid) {
          return {
            tokens: [],
            captions: null,
          };
        }

        const tokens = [];
        const captions = {};

        Array.from(mediaGrid.querySelectorAll('.sb-product-main__media-item')).forEach((item) => {
          let token = '';
          const video = item.querySelector('video.sb-product-main__media-asset--video');
          if (video instanceof HTMLVideoElement) {
            const videoSource = video.getAttribute('src') || video.dataset.sbMediaSrc || video.currentSrc || video.querySelector('source[src]')?.getAttribute('src') || '';
            const videoPoster =
              video.getAttribute('poster')
              || item.querySelector('[data-gallery-video-poster]')?.getAttribute('src')
              || '';
            if (videoSource) {
              token = videoPoster ? `video::${videoSource}::${videoPoster}` : `video::${videoSource}`;
            }
          } else {
            const image = item.querySelector('img.sb-product-main__media-asset');
            const imageSource = image?.getAttribute('src') || image?.currentSrc || '';
            if (imageSource) {
              token = `image::${imageSource}`;
            }
          }

          if (!token) return;

          tokens.push(token);
          const captionText = item.querySelector('.sb-product-main__media-caption')?.textContent?.trim() || '';
          if (captionText) {
            captions[`${tokens.length}`] = captionText;
          }
        });

        return {
          tokens,
          captions: Object.keys(captions).length ? captions : null,
        };
      };

      const initialMediaGridState = serializeMediaGridState();
      const normalizeMediaSourceForSignature = (source) => {
        const trimmedSource = String(source || '').trim();
        if (!trimmedSource) return '';

        try {
          const normalizedUrl = new URL(trimmedSource, window.location.href);
          if (normalizedUrl.pathname.startsWith('/cdn/')) {
            return `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;
          }
          return normalizedUrl.href;
        } catch (_) {
          return trimmedSource;
        }
      };

      const getMediaGallerySignatureItems = (tokens, captionsByPosition = null) => {
        if (!Array.isArray(tokens)) return [];

        const normalizedTokens = tokens
          .map((token) => (typeof token === 'string' ? token.trim() : ''))
          .filter(Boolean);

        return normalizedTokens.map((token, index) => {
          const parts = token.split('::');
          return JSON.stringify({
            type: parts[0] || '',
            source: normalizeMediaSourceForSignature(parts[1] || ''),
            poster: normalizeMediaSourceForSignature(parts[2] || ''),
            caption: getMediaCaptionByPosition(captionsByPosition, index + 1),
          });
        });
      };

      let currentMediaGallerySignatureItems = getMediaGallerySignatureItems(
        initialMediaGridState.tokens,
        initialMediaGridState.captions
      );
      let currentMediaGallerySignature = JSON.stringify(currentMediaGallerySignatureItems);

      const buildMediaGalleryItemMarkup = (token, index, captionsByPosition = null) => {
        const parts = token.split('::');
        const type = parts[0] || '';
        const source = parts[1] || '';
        const poster = parts[2] || '';
        if (!source) return '';
        const captionText = getMediaCaptionByPosition(captionsByPosition, index + 1);
        const captionMarkup = captionText
          ? `<span class=\"sb-product-main__media-caption font-caption weight-semibold\">${escapeHtml(captionText)}</span>`
          : '';

        if (type === 'video') {
          const posterMarkup = poster
            ? `<img class=\"sb-product-main__media-video-poster\" data-gallery-video-poster src=\"${escapeHtml(poster)}\" alt=\"${escapeHtml(productTitle)}\" loading=\"${index === 0 ? 'eager' : 'lazy'}\"${index === 0 ? ' fetchpriority=\"high\"' : ''} width=\"720\" height=\"720\">`
            : '';
          const posterAttribute = poster ? ` poster=\"${escapeHtml(poster)}\"` : '';
          return `<div class=\"sb-product-main__media-item sb-product-main__media-item--video\">${captionMarkup}${posterMarkup}<video class=\"sb-product-main__media-asset sb-product-main__media-asset--video\" style=\"background: var(--sb-color-always-white);\" loop muted playsinline preload=\"metadata\"${posterAttribute} src=\"${escapeHtml(source)}\"></video></div>`;
        }

        return `<div class=\"sb-product-main__media-item\">${captionMarkup}<img class=\"sb-product-main__media-asset\" src=\"${escapeHtml(source)}\" alt=\"${escapeHtml(productTitle)}\" loading=\"${index === 0 ? 'eager' : 'lazy'}\"${index === 0 ? ' fetchpriority=\"high\"' : ''} width=\"720\" height=\"720\"></div>`;
      };

      const syncMediaItemCaption = (item, captionText) => {
        if (!(item instanceof HTMLElement)) return;
        const existingCaption = item.querySelector('.sb-product-main__media-caption');
        if (!captionText) {
          existingCaption?.remove();
          return;
        }

        if (existingCaption) {
          existingCaption.textContent = captionText;
          return;
        }

        const captionElement = document.createElement('span');
        captionElement.className = 'sb-product-main__media-caption font-caption weight-semibold';
        captionElement.textContent = captionText;
        item.prepend(captionElement);
      };

      const updateMediaGalleryImageItemInPlace = (item, token, index, captionsByPosition = null) => {
        if (!(item instanceof HTMLElement)) return false;
        const parts = String(token || '').split('::');
        const type = parts[0] || '';
        const source = parts[1] || '';
        if (type !== 'image' || !source) return false;
        if (item.querySelector('video.sb-product-main__media-asset--video')) return false;

        const image = item.querySelector('img.sb-product-main__media-asset');
        if (!(image instanceof HTMLImageElement)) return false;

        item.classList.remove('sb-product-main__media-item--video');
        syncMediaItemCaption(item, getMediaCaptionByPosition(captionsByPosition, index + 1));

        image.alt = productTitle;
        image.loading = index === 0 ? 'eager' : 'lazy';
        if (index === 0) {
          image.setAttribute('fetchpriority', 'high');
        } else {
          image.removeAttribute('fetchpriority');
        }
        image.width = 720;
        image.height = 720;

        const currentSource = normalizeMediaSourceForSignature(image.getAttribute('src') || '');
        const nextSource = normalizeMediaSourceForSignature(source);
        if (currentSource !== nextSource) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = source;
        }

        return true;
      };

      const mediaGalleryImagePreloadMap = new Map();
      const getImageSourceFromMediaToken = (token) => {
        const parts = String(token || '').split('::');
        const type = parts[0] || '';
        if (type === 'image') return parts[1] || '';
        if (type === 'video') return parts[2] || '';
        return '';
      };

      const preloadMediaGalleryImageSource = (source, { decode = false } = {}) => {
        const normalizedSource = normalizeMediaSourceForSignature(source);
        if (!normalizedSource) return Promise.resolve();

        if (!mediaGalleryImagePreloadMap.has(normalizedSource)) {
          const preloadImage = new Image();
          preloadImage.decoding = 'async';
          preloadImage.src = normalizedSource;

          const loadPromise = preloadImage.complete && preloadImage.naturalWidth > 0
            ? Promise.resolve(preloadImage)
            : new Promise((resolve) => {
                preloadImage.onload = () => resolve(preloadImage);
                preloadImage.onerror = () => resolve(preloadImage);
              });

          mediaGalleryImagePreloadMap.set(normalizedSource, loadPromise);
        }

        const preloadPromise = mediaGalleryImagePreloadMap.get(normalizedSource);
        if (!decode) return preloadPromise.then(() => {});

        return preloadPromise.then((preloadImage) => {
          if (!preloadImage || typeof preloadImage.decode !== 'function') return;
          return preloadImage.decode().catch(() => {});
        });
      };

      const waitForMediaGalleryItemReady = (item) => {
        const image = item?.querySelector('img.sb-product-main__media-asset');
        if (!image) return Promise.resolve();

        const imageSource = image.getAttribute('src') || '';
        if (!imageSource) return Promise.resolve();

        return preloadMediaGalleryImageSource(imageSource, { decode: true });
      };
      let mediaGalleryRenderRequestId = 0;

      const renderMediaGallery = async (tokens, captionsByPosition = null) => {
        if (!mediaGrid || !Array.isArray(tokens)) return;
        const renderRequestId = ++mediaGalleryRenderRequestId;
        const normalizedTokens = tokens
          .map((token) => (typeof token === 'string' ? token.trim() : ''))
          .filter(Boolean);
        if (!normalizedTokens.length) return;

        const nextMediaGallerySignatureItems = getMediaGallerySignatureItems(normalizedTokens, captionsByPosition);
        const nextMediaGallerySignature = JSON.stringify(nextMediaGallerySignatureItems);
        if (nextMediaGallerySignature === currentMediaGallerySignature) return;

        const nextMarkupItems = normalizedTokens
          .map((token, index) => buildMediaGalleryItemMarkup(token, index, captionsByPosition))
          .filter(Boolean);
        const nextMarkup = nextMarkupItems.join('');
        const currentMediaItems = Array.from(mediaGrid.children).filter((item) =>
          item.classList.contains('sb-product-main__media-item')
        );

        if (nextMarkupItems.length === currentMediaItems.length && currentMediaItems.length === normalizedTokens.length) {
          await Promise.all(normalizedTokens.map((token, index) => {
            if (nextMediaGallerySignatureItems[index] === currentMediaGallerySignatureItems[index]) return;
            return preloadMediaGalleryImageSource(getImageSourceFromMediaToken(token), { decode: true });
          }));
          if (signal.aborted || renderRequestId !== mediaGalleryRenderRequestId) return;

          nextMarkupItems.forEach((markup, index) => {
            if (nextMediaGallerySignatureItems[index] === currentMediaGallerySignatureItems[index]) return;
            if (updateMediaGalleryImageItemInPlace(currentMediaItems[index], normalizedTokens[index], index, captionsByPosition)) return;

            const template = document.createElement('template');
            template.innerHTML = markup.trim();
            const nextItem = template.content.firstElementChild;
            if (nextItem) currentMediaItems[index].replaceWith(nextItem);
          });
          currentMediaGallerySignatureItems = nextMediaGallerySignatureItems;
          currentMediaGallerySignature = nextMediaGallerySignature;
          initGalleryVideos();
          return;
        }

        if (nextMarkup) {
          const template = document.createElement('template');
          template.innerHTML = nextMarkup;
          const nextMediaItems = Array.from(template.content.querySelectorAll('.sb-product-main__media-item'));
          await Promise.all(nextMediaItems.map((item) => waitForMediaGalleryItemReady(item)));
          if (signal.aborted || renderRequestId !== mediaGalleryRenderRequestId) return;
          mediaGrid.replaceChildren(...Array.from(template.content.children));
          currentMediaGallerySignatureItems = nextMediaGallerySignatureItems;
          currentMediaGallerySignature = nextMediaGallerySignature;
          initGalleryVideos();
        }
      };

      const preloadVariantLeadImages = () => {
        const leadSources = new Set();

        if (variantMediaMap && typeof variantMediaMap === 'object') {
          Object.values(variantMediaMap).forEach((tokens) => {
            if (!Array.isArray(tokens) || !tokens.length) return;
            const source = getImageSourceFromMediaToken(tokens[0]);
            if (source) leadSources.add(source);
          });
        }

        leadSources.forEach((source) => {
          preloadMediaGalleryImageSource(source);
        });
      };

      const scheduleVariantLeadImageWarmup = () => {
        let warmup;
        signal.addEventListener('abort', () => {
          if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(warmup);
          else window.clearTimeout(warmup);
        }, { once: true });
        const warm = () => {
          if (typeof window.requestIdleCallback === 'function') {
            warmup = window.requestIdleCallback(preloadVariantLeadImages, { timeout: 2500 });
            return;
          }

          warmup = window.setTimeout(preloadVariantLeadImages, 1200);
        };

        if (document.readyState === 'complete') {
          warm();
          return;
        }

        listen(window, 'load', warm, { once: true });
      };

      scheduleVariantLeadImageWarmup();

      const getBaseGalleryState = (variantId = currentVariantId) => {
        const normalizedVariantId = `${variantId || ''}`;
        const variantTokens = normalizedVariantId ? variantMediaMap?.[normalizedVariantId] : null;
        if (Array.isArray(variantTokens) && variantTokens.length) {
          return {
            tokens: [...variantTokens],
            captions: variantMediaCaptionsMap?.[normalizedVariantId] || null,
          };
        }

        return {
          tokens: [...(initialMediaGridState.tokens || [])],
          captions: initialMediaGridState.captions || null,
        };
      };

      const renderVariantMediaById = (variantId) => {
        if (!variantMediaMap || !variantId) return;
        const tokens = variantMediaMap[`${variantId}`];
        if (tokens) {
          const captionsByPosition = variantMediaCaptionsMap?.[`${variantId}`] || null;
          renderMediaGallery(tokens, captionsByPosition);
        }
      };

      const renderSelectorListMediaByVariantId = (variantId) => {
        if (!mediaGrid || !hasMultipackSelectorList || !variantId) return;

        const normalizedVariantId = `${variantId}`;
        const baseVariant = variants.find((entry) => `${entry.id}` === normalizedVariantId);
        if (baseVariant) {
          const baseGalleryState = getBaseGalleryState(normalizedVariantId);
          if (baseGalleryState.tokens.length) {
            renderMediaGallery(baseGalleryState.tokens, baseGalleryState.captions);
          }
        }
      };

      const renderInTheBoxByVariantId = (variantId) => {
        if (!inTheBoxContainer || !inTheBoxGrid || !variantInTheBoxMap || !variantId) return;
        const items = Array.isArray(variantInTheBoxMap[`${variantId}`]) ? variantInTheBoxMap[`${variantId}`] : [];
        if (!items.length) {
          inTheBoxContainer.hidden = true;
          return;
        }

        const nextMarkup = items
          .map((item) => {
            const title = typeof item?.title === 'string' ? item.title : '';
            const mediaUrl = typeof item?.mediaUrl === 'string' ? item.mediaUrl : '';
            if (!title) return '';
            if (mediaUrl) {
              return `<article class="sb-product-main__in-the-box-item"><img class="sb-product-main__in-the-box-image" src="${escapeHtml(mediaUrl)}" alt="${escapeHtml(title)}" loading="lazy" width="160" height="160"><p class="sb-product-main__in-the-box-text font-caption weight-regular">${escapeHtml(title)}</p></article>`;
            }
            return `<article class="sb-product-main__in-the-box-item"><div class="sb-product-main__in-the-box-image" aria-hidden="true"></div><p class="sb-product-main__in-the-box-text font-caption weight-regular">${escapeHtml(title)}</p></article>`;
          })
          .join('');

        if (!nextMarkup) {
          inTheBoxContainer.hidden = true;
          return;
        }

        inTheBoxGrid.innerHTML = nextMarkup;
        if (inTheBoxDivider) inTheBoxDivider.hidden = false;
        inTheBoxContainer.hidden = false;
      };

      const formatAddonPrice = (centsValue) => formatMoney(centsValue);

      const initAddonItem = (item) => {
        if (!item) return;

        const variantsElement = item.querySelector('[data-addon-variants-json]');
        const mediaMapElement = item.querySelector('[data-addon-media-map-json]');
        const hoverMapElement = item.querySelector('[data-addon-hover-map-json]');
        const addButtonElement = item.querySelector('[data-addon-add-to-cart]');
        const addButtonLabelElement = item.querySelector('[data-addon-add-label]');
        const imageElement = item.querySelector('[data-addon-image]');
        const imageLinkElement = item.querySelector('.sb-product-main__addon-image-link');
        const productLinkElements = Array.from(item.querySelectorAll('[data-addon-product-link]'));
        const comparePriceElement = item.querySelector('[data-addon-compare-price]');
        const currentPriceElement = item.querySelector('[data-addon-current-price]');
        const optionGroups = Array.from(item.querySelectorAll('[data-addon-option-position]'));
        if (!variantsElement || !addButtonElement) return;

        let addonVariants = [];
        try {
          addonVariants = JSON.parse(variantsElement.textContent || '[]');
        } catch (error) {
          return;
        }
        if (!Array.isArray(addonVariants) || !addonVariants.length) return;

        let addonMediaMap = {};
        if (mediaMapElement) {
          try {
            addonMediaMap = JSON.parse(mediaMapElement.textContent || '{}') || {};
          } catch (error) {
            addonMediaMap = {};
          }
        }
        let addonHoverMap = {};
        if (hoverMapElement) {
          try {
            addonHoverMap = JSON.parse(hoverMapElement.textContent || '{}') || {};
          } catch (error) {
            addonHoverMap = {};
          }
        }

        const selectedFromMarkup = addonVariants.find(
          (variant) => `${variant.id}` === `${addButtonElement.dataset.addonVariantId || ''}`
        );
        const selectedVariant = selectedFromMarkup || addonVariants[0];
        let selectedOptions = Array.isArray(selectedVariant?.options) ? [...selectedVariant.options] : [];
        let isAddonImageHovering = false;
        let previewBaseImageUrl = '';
        let currentBaseImageUrl = '';
        let currentHoverImageUrl = '';
        const addonImagePromiseCache = new Map();
        let addonImageSwapToken = 0;
        let addonImagePoolWarmed = false;

        const ensureAddonImageReady = (source) => {
          const normalizedSource = `${source || ''}`.trim();
          if (!normalizedSource) return Promise.resolve();
          if (addonImagePromiseCache.has(normalizedSource)) return addonImagePromiseCache.get(normalizedSource);

          const promise = new Promise((resolve) => {
            const image = new Image();
            const done = () => resolve();
            image.decoding = 'async';
            image.onload = done;
            image.onerror = done;
            image.src = normalizedSource;
            if (image.complete) resolve();
          });

          addonImagePromiseCache.set(normalizedSource, promise);
          return promise;
        };

        const preloadAddonImage = (source) => {
          void ensureAddonImageReady(source);
        };

        const warmAddonImagePool = () => {
          if (addonImagePoolWarmed) return;
          addonImagePoolWarmed = true;
          Object.values(addonMediaMap || {}).forEach((source) => preloadAddonImage(source));
          Object.values(addonHoverMap || {}).forEach((source) => preloadAddonImage(source));
        };

        const buildAddonVariantUrl = (variantId) => {
          const baseUrl = item.dataset.addonProductUrl || '';
          if (!baseUrl || !variantId) return baseUrl;
          return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${variantId}`;
        };

        const applyAddonLinks = (variantId) => {
          const nextUrl = buildAddonVariantUrl(variantId);
          if (!nextUrl || !productLinkElements.length) return;
          productLinkElements.forEach((link) => {
            link.href = nextUrl;
          });
        };

        const applyAddonImageState = () => {
          if (!imageElement || imageElement.tagName !== 'IMG') return;
          const baseImageSource = previewBaseImageUrl || currentBaseImageUrl;
          const nextSource = isAddonImageHovering && currentHoverImageUrl ? currentHoverImageUrl : baseImageSource;
          const swapToken = ++addonImageSwapToken;
          if (!nextSource || imageElement.src === nextSource) return;

          ensureAddonImageReady(nextSource).then(() => {
            if (signal.aborted || addonImageSwapToken !== swapToken) return;
            if (imageElement.src !== nextSource) {
              imageElement.src = nextSource;
            }
          });
        };

        const getGroupOptionIndex = (group) => {
          const optionPosition = Number.parseInt(group.dataset.addonOptionPosition || '', 10);
          if (!Number.isFinite(optionPosition) || optionPosition < 1) return -1;
          return optionPosition - 1;
        };

        const setGroupSelection = (group, selectedValue) => {
          const isColorGroup = group.dataset.addonOptionType === 'color';
          group.querySelectorAll('[data-addon-option-value]').forEach((button) => {
            const isSelected = button.dataset.addonOptionValue === selectedValue;
            button.classList.toggle('is-active', !isColorGroup && isSelected);
            button.classList.toggle('is-selected', isColorGroup && isSelected);
            button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });
        };

        const nonColorGroups = optionGroups.filter((group) => group.dataset.addonOptionType !== 'color');

        const updateOptionAvailability = () => {
          optionGroups.forEach((optionGroup) => {
            const optionIndex = getGroupOptionIndex(optionGroup);
            if (optionIndex < 0) return;
            const isColorOptionGroup = optionGroup.dataset.addonOptionType === 'color';

            const buttons = Array.from(optionGroup.querySelectorAll('[data-addon-option-value]'));
            let visibleValueCount = 0;

            buttons.forEach((button) => {
              const nextValue = button.dataset.addonOptionValue || '';
              const hasMatchingVariant = addonVariants.some((variant) => {
                if (!Array.isArray(variant.options)) return false;
                if (!variant.available) return false;
                if (variant.options[optionIndex] !== nextValue) return false;
                return variant.options.every((optionValue, index) => {
                  if (index === optionIndex) return true;
                  const comparisonGroup = optionGroups.find((group) => getGroupOptionIndex(group) === index);
                  if (!isColorOptionGroup && comparisonGroup?.dataset.addonOptionType === 'color') return true;
                  const selectedValue = selectedOptions[index];
                  return !selectedValue || optionValue === selectedValue;
                });
              });

              button.hidden = !hasMatchingVariant;
              button.classList.toggle('is-hidden', !hasMatchingVariant);
              button.setAttribute('aria-hidden', hasMatchingVariant ? 'false' : 'true');

              if (hasMatchingVariant) {
                visibleValueCount += 1;
              }
            });

            optionGroup.hidden = visibleValueCount <= 1;
            setGroupSelection(optionGroup, selectedOptions[optionIndex] || '');
          });
        };

        signal.addEventListener('abort', () => {
          if (addButtonElement.dataset.cartSubmitPending !== 'true') return;
          delete addButtonElement.dataset.cartSubmitPending;
          addButtonElement.disabled = addButtonElement.dataset.variantAvailable === 'false';
        }, { once: true });

        const applyAddonVariant = (variant) => {
          if (!variant) return;
          addButtonElement.dataset.addonVariantId = `${variant.id}`;
          addButtonElement.dataset.variantAvailable = String(Boolean(variant.available));
          addButtonElement.disabled = addButtonElement.dataset.cartSubmitPending === 'true' || !variant.available;
          const addonAvailableLabel = addButtonElement.dataset.labelAvailable || addLabel;
          const addonUnavailableLabel = addButtonElement.dataset.labelUnavailable || soldOutLabel;
          const addonNextLabel = variant.available ? addonAvailableLabel : addonUnavailableLabel;
          addButtonElement.setAttribute('aria-label', addonNextLabel);
          if (addButtonLabelElement) {
            addButtonLabelElement.textContent = addonNextLabel;
          }

          const addonPricing = resolveDisplayPrice(
            variant,
            `${item.dataset.addonItemId || ''}` || `${item.dataset.addonProductId || ''}`
          );
          const hasComparePrice =
            Number.isFinite(addonPricing.comparePriceCents)
            && addonPricing.comparePriceCents > addonPricing.currentPriceCents;
          if (comparePriceElement) {
            if (hasComparePrice) {
              comparePriceElement.textContent = formatAddonPrice(addonPricing.comparePriceCents);
              comparePriceElement.hidden = false;
            } else {
              comparePriceElement.textContent = '';
              comparePriceElement.hidden = true;
            }
          }
          if (currentPriceElement) {
            currentPriceElement.textContent = formatAddonPrice(addonPricing.currentPriceCents);
          }

          const nextImageUrl = addonMediaMap?.[`${variant.id}`] || '';
          if (nextImageUrl) {
            currentBaseImageUrl = nextImageUrl;
          } else if (imageElement && imageElement.tagName === 'IMG') {
            currentBaseImageUrl = imageElement.currentSrc || imageElement.src || '';
          }
          previewBaseImageUrl = '';
          currentHoverImageUrl = addonHoverMap?.[`${variant.id}`] || '';
          preloadAddonImage(currentBaseImageUrl);
          preloadAddonImage(currentHoverImageUrl);
          applyAddonImageState();
          applyAddonLinks(variant.id);

          if (Array.isArray(variant.options)) {
            selectedOptions = [...variant.options];
            optionGroups.forEach((group) => {
              const optionIndex = getGroupOptionIndex(group);
              if (optionIndex < 0) return;
              setGroupSelection(group, selectedOptions[optionIndex] || '');
            });
            updateOptionAvailability();
          }
        };

        const findAddonVariantByOptions = (optionsSnapshot, { allowNonColorFallback = false } = {}) => {
          if (!Array.isArray(optionsSnapshot) || !optionsSnapshot.length) return null;

          const exactMatch = addonVariants.find((variant) => {
            if (!Array.isArray(variant.options) || variant.options.length !== optionsSnapshot.length) return false;
            if (!variant.available) return false;
            return variant.options.every((optionValue, index) => optionValue === optionsSnapshot[index]);
          });
          if (exactMatch) return exactMatch;

          if (!allowNonColorFallback) return null;

          return addonVariants.find((variant) => {
            if (!Array.isArray(variant.options) || !variant.available) return false;
            return nonColorGroups.every((nonColorGroup) => {
              const nonColorIndex = getGroupOptionIndex(nonColorGroup);
              if (nonColorIndex < 0) return true;
              return variant.options[nonColorIndex] === optionsSnapshot[nonColorIndex];
            });
          });
        };

        optionGroups.forEach((group) => {
          const optionIndex = getGroupOptionIndex(group);
          if (optionIndex < 0) return;
          const buttons = Array.from(group.querySelectorAll('[data-addon-option-value]'));
          if (!buttons.length) return;

          const defaultValue = selectedOptions[optionIndex] || buttons[0].dataset.addonOptionValue || '';
          selectedOptions[optionIndex] = defaultValue;
          setGroupSelection(group, defaultValue);

            buttons.forEach((button) => {
              listen(button, 'click', () => {
                if (button.hidden) return;
                warmAddonImagePool();

              const nextValue = button.dataset.addonOptionValue || '';
              selectedOptions[optionIndex] = nextValue;
              setGroupSelection(group, nextValue);

              if (group.dataset.addonOptionType !== 'color') {
                updateOptionAvailability();
              }

              const matchedVariant = findAddonVariantByOptions(selectedOptions, {
                allowNonColorFallback: group.dataset.addonOptionType !== 'color',
              });

              if (matchedVariant) {
                applyAddonVariant(matchedVariant);
              }
              });

              listen(button, 'mouseenter', () => {
                if (button.hidden) return;
                warmAddonImagePool();
                const hoveredValue = button.dataset.addonOptionValue || '';
                if (!hoveredValue) return;
                const previewOptions = [...selectedOptions];
                previewOptions[optionIndex] = hoveredValue;
                const previewVariant = findAddonVariantByOptions(previewOptions, {
                  allowNonColorFallback: group.dataset.addonOptionType !== 'color',
                });
                const previewUrl = previewVariant ? addonMediaMap?.[`${previewVariant.id}`] : '';
                previewBaseImageUrl = previewUrl || '';
                applyAddonImageState();
              });

              listen(button, 'mouseleave', () => {
                previewBaseImageUrl = '';
                applyAddonImageState();
              });
            });
          });

        listen(addButtonElement, 'click', async () => {
          const variantId = Number(addButtonElement.dataset.addonVariantId || '0');
          if (!variantId || addButtonElement.disabled) return;

          addButtonElement.dataset.cartSubmitPending = 'true';
          addButtonElement.disabled = true;

          try {
            if (window.SBCart && typeof window.SBCart.add === 'function') {
              await window.SBCart.add(variantId, {
                sourceElement: addButtonElement,
                imageSrc: currentBaseImageUrl || '',
              });
            } else {
              throw new Error('SBCart unavailable');
            }
          } catch (_) {
            // The shared cart owns request error handling.
          } finally {
            if (!signal.aborted) {
              delete addButtonElement.dataset.cartSubmitPending;
              addButtonElement.disabled = addButtonElement.dataset.variantAvailable === 'false';
            }
          }
        });

        refreshPrices.push(() => {
          const activeVariant = addonVariants.find(
            (variant) => `${variant.id}` === `${addButtonElement.dataset.addonVariantId || ''}`
          ) || selectedVariant;
          applyAddonVariant(activeVariant);
        });

        if (imageLinkElement) {
          listen(imageLinkElement, 'mouseenter', () => {
            warmAddonImagePool();
            isAddonImageHovering = true;
            applyAddonImageState();
          });
          listen(imageLinkElement, 'focusin', () => {
            warmAddonImagePool();
          });
          listen(imageLinkElement, 'mouseleave', () => {
            isAddonImageHovering = false;
            applyAddonImageState();
          });
        }

        applyAddonVariant(selectedVariant);
      };

      const setSelectorListSelection = (selectedItem) => {
        if (!selectorListItems.length) return;
        selectorListItems.forEach((item) => {
          const isSelected = item === selectedItem;
          item.classList.toggle('is-selected', isSelected);
          item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      };

      const setSelectorListSelectionByOptionValue = (selectedValue) => {
        if (!selectorListItems.length) return;
        selectorListItems.forEach((item) => {
          const isSelected = item.dataset.selectorOptionValue === selectedValue;
          item.classList.toggle('is-selected', isSelected);
          item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      };

      const ensureSelectorListComparePriceElement = (item) => {
        const existingCompareElement = item.querySelector('.sb-product-main__price--compare');
        if (existingCompareElement) return existingCompareElement;

        const priceValuesElement = item.querySelector('.sb-product-main__price-values');
        const currentPriceElement = item.querySelector('.sb-product-main__price--current');
        if (!priceValuesElement || !currentPriceElement) return null;

        const compareElement = document.createElement('span');
        compareElement.className = 'sb-product-main__price sb-product-main__price--compare font-body weight-regular';
        compareElement.hidden = true;
        compareElement.innerHTML = `
          <span class="sb-product-main__visually-hidden" data-product-price-accessibility-label></span>
          <span data-product-price-value></span>
        `;
        priceValuesElement.insertBefore(compareElement, currentPriceElement);
        return compareElement;
      };

      const resolveSelectorListVariant = (item) => {
        if (item.dataset.selectorListMode !== 'variant') return null;
        const selectedOptionValue = item.dataset.selectorOptionValue || '';
        if (selectedOptionValue && selectorListOptionIndex >= 0) {
          const nextOptions = [...selectedOptions];
          nextOptions[selectorListOptionIndex] = selectedOptionValue;
          const optionVariants = variants.filter((entry) =>
            Array.isArray(entry.options) && entry.options[selectorListOptionIndex] === selectedOptionValue
          );
          const exactVariant = optionVariants.find((entry) =>
            entry.options.length === nextOptions.length
            && entry.options.every((optionValue, index) => optionValue === nextOptions[index])
          );
          const preserveCurrentOption = selectedOptionValue === selectedOptions[selectorListOptionIndex];
          const variant = exactVariant && (exactVariant.available || preserveCurrentOption) ? exactVariant
            : optionVariants.find((entry) => entry.available) || exactVariant || optionVariants[0];
          if (variant) return variant;
        }
        return variants.find((entry) => `${entry.id}` === item.dataset.variantId) || null;
      };

      const syncSelectorListItemPricing = (item) => {
        if (!item) return;
        const variant = resolveSelectorListVariant(item);
        if (variant) {
          item.dataset.variantId = `${variant.id}`;
          item.dataset.variantAvailable = String(Boolean(variant.available));
          item.dataset.variantPrice = `${Number(variant.price) || 0}`;
          item.dataset.variantComparePrice = `${Number(variant.compare_at_price) || 0}`;
          item.dataset.variantProductId = `${variant.product_id || productId}`;
        }

        const quantity = Math.max(1, Number.parseInt(item.dataset.multipackQuantity || '1', 10) || 1);

        const itemPricing = resolveDisplayPrice(
          {
            price: Number(item.dataset.variantPrice || '0'),
            compare_at_price: Number(item.dataset.variantComparePrice || '0'),
          },
          item.dataset.variantProductId || productId,
          quantity
        );

        const hasComparePrice =
          Number.isFinite(itemPricing.comparePriceCents)
          && itemPricing.comparePriceCents > itemPricing.currentPriceCents;
        const currentPriceNode = item.querySelector('.sb-product-main__price--current');
        if (currentPriceNode) {
          const currentPriceLabel = hasComparePrice ? salePriceLabel : priceLabel;
          setPriceElement(currentPriceNode, currentPriceLabel, formatMoney(itemPricing.currentPriceCents));
        }

        let comparePriceNode = item.querySelector('.sb-product-main__price--compare');

        if (hasComparePrice) {
          if (!comparePriceNode) {
            comparePriceNode = ensureSelectorListComparePriceElement(item);
          }
          if (comparePriceNode) {
            setPriceElement(comparePriceNode, regularPriceLabel, formatMoney(itemPricing.comparePriceCents));
            comparePriceNode.hidden = false;
          }
        } else if (comparePriceNode) {
          setPriceElement(comparePriceNode, regularPriceLabel, '');
          comparePriceNode.hidden = true;
        }

        const savingsBadgeNode = item.querySelector('[data-selector-list-savings-badge]');
        if (savingsBadgeNode) {
          const comparePriceCents = Number(itemPricing.comparePriceCents) || 0;
          const savingsPercent = comparePriceCents > 0
            ? Math.round(((comparePriceCents - Number(itemPricing.currentPriceCents || 0)) / comparePriceCents) * 100)
            : 0;
          savingsBadgeNode.textContent = savingsPercent > 0
            ? savePercentTemplate.replace('__PERCENT__', `${savingsPercent}`)
            : '';
          savingsBadgeNode.hidden = savingsPercent <= 0;
        }
      };

      const applyPurchaseState = (purchaseData, { announce = true } = {}) => {
        if (!purchaseData) return;
        const resolvedPricing = resolveDisplayPrice(
          purchaseData,
          purchaseData.product_id || productId,
          purchaseData.quantity || 1
        );
        const hasComparePrice =
          Number.isFinite(resolvedPricing.comparePriceCents)
          && resolvedPricing.comparePriceCents > resolvedPricing.currentPriceCents;
        if (currentPriceElement) {
          const currentPriceLabel = hasComparePrice ? salePriceLabel : priceLabel;
          setPriceElement(currentPriceElement, currentPriceLabel, formatMoney(resolvedPricing.currentPriceCents));
        }
        if (comparePriceElement) {
          if (hasComparePrice) {
            setPriceElement(comparePriceElement, regularPriceLabel, formatMoney(resolvedPricing.comparePriceCents));
            comparePriceElement.hidden = false;
          } else {
            setPriceElement(comparePriceElement, regularPriceLabel, '');
            comparePriceElement.hidden = true;
          }
        }
        if (savingsBadgeElement) {
          if (hasComparePrice) {
            const compareAt = Number(resolvedPricing.comparePriceCents) || 0;
            const savingsPercent = compareAt > 0
              ? Math.round(((compareAt - Number(resolvedPricing.currentPriceCents || 0)) / compareAt) * 100)
              : 0;
            savingsBadgeElement.textContent = savePercentTemplate.replace('__PERCENT__', `${savingsPercent}`);
            savingsBadgeElement.hidden = false;
          } else {
            savingsBadgeElement.hidden = true;
          }
        }
        if (monthlyPriceElement) {
          monthlyPriceElement.textContent = formatMonthlyMoney(resolvedPricing.currentPriceCents);
        }

        if (addButton) {
          addButton.dataset.variantAvailable = String(Boolean(purchaseData.available));
          addButton.disabled = addButton.dataset.cartSubmitPending === 'true' || !purchaseData.available;
          const availableLabel = addButton.dataset.labelAvailable || addToCartLabel;
          const unavailableLabel = addButton.dataset.labelUnavailable || soldOutLabel;
          const nextLabel = purchaseData.available ? availableLabel : unavailableLabel;
          if (addButtonLabel) addButtonLabel.textContent = nextLabel;
          addButton.setAttribute('aria-label', nextLabel);
        }

        if (stickyCurrentPriceElement) {
          const currentPriceLabel = hasComparePrice ? salePriceLabel : priceLabel;
          setPriceElement(stickyCurrentPriceElement, currentPriceLabel, formatMoney(resolvedPricing.currentPriceCents));
        }
        if (stickyComparePriceElement) {
          if (hasComparePrice) {
            setPriceElement(stickyComparePriceElement, regularPriceLabel, formatMoney(resolvedPricing.comparePriceCents));
            stickyComparePriceElement.hidden = false;
          } else {
            setPriceElement(stickyComparePriceElement, regularPriceLabel, '');
            stickyComparePriceElement.hidden = true;
          }
        }
        if (stickyAddButton) {
          stickyAddButton.disabled = !purchaseData.available;
          const availableLabel = stickyAddButton.dataset.labelAvailable || addToCartLabel;
          const unavailableLabel = stickyAddButton.dataset.labelUnavailable || soldOutLabel;
          const nextLabel = purchaseData.available ? availableLabel : unavailableLabel;
          if (stickyAddButtonLabel) stickyAddButtonLabel.textContent = nextLabel;
          stickyAddButton.setAttribute('aria-label', nextLabel);
        }

        if (stickyBottomCurrentPriceElement) {
          const currentPriceLabel = hasComparePrice ? salePriceLabel : priceLabel;
          setPriceElement(stickyBottomCurrentPriceElement, currentPriceLabel, formatMoney(resolvedPricing.currentPriceCents));
        }
        if (stickyBottomComparePriceElement) {
          if (hasComparePrice) {
            setPriceElement(stickyBottomComparePriceElement, regularPriceLabel, formatMoney(resolvedPricing.comparePriceCents));
            stickyBottomComparePriceElement.hidden = false;
          } else {
            setPriceElement(stickyBottomComparePriceElement, regularPriceLabel, '');
            stickyBottomComparePriceElement.hidden = true;
          }
        }
        if (stickyBottomAddButton) {
          stickyBottomAddButton.disabled = !purchaseData.available;
          const nextLabel = purchaseData.available ? addLabel : soldOutLabel;
          if (stickyBottomAddButtonLabel) stickyBottomAddButtonLabel.textContent = nextLabel;
          stickyBottomAddButton.setAttribute('aria-label', purchaseData.available ? addToCartLabel : soldOutLabel);
        }

        if (announce) {
          updatePriceLiveRegion({
            resolvedPricing,
            hasComparePrice,
            available: purchaseData.available,
          });
        }

        applyStickyImageSource(getStickyImageSourceByVariantId(purchaseData.id));
        applyStickyHoverImageSource(purchaseData.id);
      };

      const setGroupSelection = (group, selectedValue) => {
        const isColorGroup = group.dataset.optionType === 'color';
        const colorLabel = group.querySelector('[data-color-selected-label]');
        if (isColorGroup && colorLabel) {
          colorLabel.textContent = selectedValue || '';
        }
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          const isSelected = button.dataset.optionValue === selectedValue;
          if (isColorGroup) {
            button.classList.toggle('is-selected', isSelected);
          } else {
            button.classList.toggle('is-active', isSelected);
          }
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      };

      const getGroupOptionIndex = (group) => {
        const optionPosition = Number.parseInt(group.dataset.optionPosition || '', 10);
        if (!Number.isFinite(optionPosition) || optionPosition < 1) return -1;
        return optionPosition - 1;
      };

      const hasMultipackSelectorList = selectorListItems.some((item) => item.dataset.selectorListMode === 'multipack');

      const updateOptionAvailability = () => {
        optionGroups.forEach((optionGroup) => {
          const optionIndex = getGroupOptionIndex(optionGroup);
          if (optionIndex < 0) return;
          const isColorOptionGroup = optionGroup.dataset.optionType === 'color';

          const buttons = Array.from(optionGroup.querySelectorAll('[data-option-value]'));

          buttons.forEach((button) => {
            const nextValue = button.dataset.optionValue || '';
            const hasMatchingVariant = variants.some((variant) => {
              if (!Array.isArray(variant.options)) return false;
              if (!variant.available) return false;
              if (variant.options[optionIndex] !== nextValue) return false;
              return variant.options.every((optionValue, index) => {
                if (index === optionIndex) return true;
                const comparisonGroup = optionGroups.find((group) => getGroupOptionIndex(group) === index);
                if (!isColorOptionGroup && comparisonGroup?.dataset.optionType === 'color') return true;
                const selectedValue = selectedOptions[index];
                return !selectedValue || optionValue === selectedValue;
              });
            });

            const isCurrentValue = nextValue === selectedOptions[optionIndex];
            button.disabled = !hasMatchingVariant;
            button.hidden = !hasMatchingVariant && !isCurrentValue;
            button.classList.toggle('is-hidden', button.hidden);
            button.setAttribute('aria-hidden', String(button.hidden));
          });

          const visibleSwatchCount = buttons.filter(
            (button) => !button.hidden && !button.classList.contains('is-hidden')
          ).length;
          optionGroup
            .querySelector('.sb-product-main__color-swatches')
            ?.style.setProperty('--sb-visible-color-swatches', `${visibleSwatchCount}`);

          setGroupSelection(optionGroup, selectedOptions[optionIndex] || '');
        });
      };

      const handleVariantUpdate = (variant, sourceId = '') => {
        if (!variant) return;

        if (hasMultipackSelectorList) {
          selectorListItems.forEach((item) => {
            const quantity = Math.max(1, Number.parseInt(item.dataset.multipackQuantity || '1', 10) || 1);
            item.dataset.variantId = `${variant.id}`;
            item.dataset.variantAvailable = variant.available ? 'true' : 'false';
            item.dataset.variantProductId = `${variant.product_id || productId}`;
            item.dataset.variantPrice = `${(Number(variant.price) || 0) * quantity}`;
            item.dataset.variantComparePrice = `${(Number(variant.compare_at_price) || 0) * quantity}`;
            syncSelectorListItemPricing(item);
          });

          const selectedQuantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
          const selectedItem = selectorListItems.find(
            (item) => Number.parseInt(item.dataset.multipackQuantity || '1', 10) === selectedQuantity
          ) || selectorListItems[0];
          if (selectedItem) {
            setSelectorListSelection(selectedItem);
            const purchaseData = {
              id: Number(variant.id),
              available: selectedItem.dataset.variantAvailable === 'true',
              product_id: selectedItem.dataset.variantProductId || productId,
              price: Number(selectedItem.dataset.variantPrice || '0'),
              compare_at_price: Number(selectedItem.dataset.variantComparePrice || '0'),
              quantity: selectedQuantity,
            };
            variantSelect.value = `${purchaseData.id}`;
            applyPurchaseState(purchaseData, {
              announce: sourceId !== 'initial',
            });
          }
        } else {
          applyPurchaseState(variant, {
            announce: sourceId !== 'initial',
          });
        }

        if (Array.isArray(variant.options)) {
          selectedOptions = [...variant.options];
          optionGroups.forEach((group) => {
            const optionPosition = Number.parseInt(group.dataset.optionPosition || '', 10);
            if (!Number.isFinite(optionPosition) || optionPosition < 1) return;
            const optionValue = selectedOptions[optionPosition - 1] || '';
            setGroupSelection(group, optionValue);
          });
          updateOptionAvailability();
          if (!hasMultipackSelectorList) selectorListItems.forEach(syncSelectorListItemPricing);
        }

        renderVariantMediaById(variant.id);
        renderInTheBoxByVariantId(variant.id);

        if (variantExplainer) {
          const explainerTemplate = variantExplainerTemplates.get(`${variant.id}`) || defaultVariantExplainer;
          const explainerHtml = explainerTemplate?.innerHTML.trim() || '';
          if (variantExplainer.innerHTML.trim() !== explainerHtml) variantExplainer.innerHTML = explainerHtml;
          variantExplainer.hidden = !explainerHtml;
        }

        if (selectorListOptionIndex >= 0 && Array.isArray(variant.options)) {
          const selectedOptionValue = variant.options[selectorListOptionIndex] || '';
          setSelectorListSelectionByOptionValue(selectedOptionValue);
        } else if (!hasMultipackSelectorList) {
          const matchingSelectorItem = selectorListItems.find((item) => item.dataset.variantId === `${variant.id}`);
          if (matchingSelectorItem) setSelectorListSelection(matchingSelectorItem);
        }
      };

      listen(root, ThemeEvents.variantUpdate, (event) => {
        const variant = event?.detail?.resource;
        if (!variant) return;
        handleVariantUpdate(variant, event?.detail?.sourceId || '');
      });

      const applyVariant = (variant, options = {}) => {
        if (!variant) return;
        const {
          sourceId = 'product-main',
          syncUrl = true,
        } = options;

        if (hasVariantInitialized && `${variant.id}` === `${currentVariantId}`) {
          if (syncUrl) syncVariantUrl(variant.id);
          return;
        }

        hasVariantInitialized = true;
        currentVariantId = `${variant.id}`;
        if (Array.isArray(variant.options)) {
          selectedOptions = [...variant.options];
        }
        variantSelect.value = `${variant.id}`;
        dispatchVariantSelectedEvent(variant, sourceId);
        dispatchVariantUpdateEvent(variant, sourceId);
        if (syncUrl) {
          syncVariantUrl(variant.id);
        }
      };

      listen(variantSelect, 'change', () => {
        const matchedVariant = variants.find((variant) => `${variant.id}` === `${variantSelect.value}`);
        if (!matchedVariant) return;
        if (`${matchedVariant.id}` === `${currentVariantId}`) return;
        applyVariant(matchedVariant, {
          sourceId: 'product-form-input',
        });
      });

      optionGroups.forEach((group) => {
        const optionPosition = Number.parseInt(group.dataset.optionPosition || '', 10);
        if (!Number.isFinite(optionPosition) || optionPosition < 1) return;
        const optionIndex = optionPosition - 1;
        const buttons = Array.from(group.querySelectorAll('[data-option-value]'));
        if (!buttons.length) return;

        const defaultValue = selectedOptions[optionIndex] || buttons[0].dataset.optionValue || '';
        selectedOptions[optionIndex] = defaultValue;
        setGroupSelection(group, defaultValue);

        buttons.forEach((button) => {
          listen(button, 'click', () => {
            if (button.hidden || button.disabled) return;
            const nextValue = button.dataset.optionValue || '';
            selectedOptions[optionIndex] = nextValue;
            const matchedVariant = variants.find((variant) => {
              if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) return false;
              if (!variant.available) return false;
              return variant.options.every((optionValue, index) => optionValue === selectedOptions[index]);
            });

            if (matchedVariant) {
                applyVariant(matchedVariant, {
                  sourceId: 'product-options',
                });
                return;
            }

            if (group.dataset.optionType !== 'color') {
              const fallbackVariant = variants.find((variant) => {
                if (!Array.isArray(variant.options)) return false;
                if (!variant.available) return false;
                return variant.options.every((optionValue, index) => {
                  const optionGroup = optionGroups.find((entry) => getGroupOptionIndex(entry) === index);
                  if (optionGroup?.dataset.optionType === 'color') return true;
                  return optionValue === selectedOptions[index];
                });
              });
              if (fallbackVariant) {
                applyVariant(fallbackVariant, {
                  sourceId: 'product-options-fallback',
                });
              }
            }
          });
        });
      });

      if (selectorListItems.length) {
        const applySelectorListItemSelection = (item, { sourceId = 'selector-list', syncUrl = true } = {}) => {
          if (!item) return;
          const variantId = item.dataset.variantId || '';
          if (!variantId) return;
          const variantMode = item.dataset.selectorListMode || '';
          setSelectorListSelection(item);

          if (variantMode === 'variant') {
            const variant = resolveSelectorListVariant(item);
            if (variant) {
              applyVariant(variant, {
                sourceId,
                syncUrl,
              });
            }
            return;
          }

          const purchaseData = {
            id: Number(variantId),
            available: item.dataset.variantAvailable === 'true',
            product_id: item.dataset.variantProductId || productId,
            price: Number(item.dataset.variantPrice || '0'),
            compare_at_price: Number(item.dataset.variantComparePrice || '0'),
            quantity: Math.max(1, Number.parseInt(item.dataset.multipackQuantity || '1', 10) || 1),
          };
          if (quantityInput) {
            quantityInput.value = `${purchaseData.quantity}`;
          }
          variantSelect.value = `${purchaseData.id}`;
          applyPurchaseState(purchaseData, {
            announce: sourceId !== 'selector-list-initial',
          });
          renderSelectorListMediaByVariantId(purchaseData.id);
        };

        selectorListItems.forEach((item) => {
          syncSelectorListItemPricing(item);
          listen(item, 'click', () => {
            applySelectorListItemSelection(item);
          });
        });

        const selectedListItem =
          selectorListItems.find((item) => item.classList.contains('is-selected')) || selectorListItems[0];
        if (selectedListItem) {
          applySelectorListItemSelection(selectedListItem, {
            sourceId: 'selector-list-initial',
            syncUrl: false,
          });
        }
      }

      const initialVariant = variants.find((variant) => `${variant.id}` === `${currentVariantId}`) || variants[0];
      if (initialVariant && !hasMultipackSelectorList && !hasVariantInitialized) {
        applyVariant(initialVariant, {
          sourceId: 'initial',
          syncUrl: false,
        });
      } else {
        updateOptionAvailability();
        if (variantInTheBoxMap && currentVariantId) {
          renderInTheBoxByVariantId(currentVariantId);
        }
      }

      initGalleryVideos();

      const addonItems = Array.from(root.querySelectorAll('[data-addon-item]'));
      addonItems.forEach((item) => initAddonItem(item));

      refreshPrices.push(() => {
        const activeVariant = variants.find((variant) => `${variant.id}` === `${currentVariantId}`) || variants[0];
        if (activeVariant) {
          const quantity = hasMultipackSelectorList
            ? Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1)
            : 1;
          applyPurchaseState({
            ...activeVariant,
            price: (Number(activeVariant.price) || 0) * quantity,
            compare_at_price: (Number(activeVariant.compare_at_price) || 0) * quantity,
            quantity,
          }, { announce: false });
        }
        selectorListItems.forEach((item) => syncSelectorListItemPricing(item));
      });
    };

    // Only connected products receive price updates. Removed quick-add roots
    // must not remain captured by document listeners.
    document.addEventListener('sb:storefront-pricing:changed', () => {
      document.querySelectorAll('.sb-product-main').forEach((root) => {
        pricingRefreshers.get(root)?.forEach((refresh) => refresh());
      });
    });

    const productRootsInScope = (scope) => {
      if (!(scope instanceof Element || scope instanceof Document)) return [];
      const roots = [...scope.querySelectorAll('.sb-product-main')];
      if (scope instanceof Element && scope.matches('.sb-product-main')) roots.unshift(scope);
      return roots;
    };

    window.SBProductMain = window.SBProductMain || {};
    window.SBProductMain.initProductRoot = initProductRoot;
    window.SBProductMain.initProductScope = (scope = document) => {
      productRootsInScope(scope).forEach((root) => initProductRoot(root));
    };
    window.SBProductMain.destroyProductScope = (scope) => {
      productRootsInScope(scope).forEach((root) => productLifetimes.get(root)?.controller.abort());
    };
    document.addEventListener('shopify:section:unload', (event) => {
      window.SBProductMain.destroyProductScope(event.target);
      // The sticky top bar is a separate section owned by the page product.
      if (!(event.target instanceof Element)) return;
      productRootsInScope(document).forEach((root) => {
        const lifetime = productLifetimes.get(root);
        if (lifetime?.stickyTopBarRoot && event.target.contains(lifetime.stickyTopBarRoot)) {
          // Unload also fires for deletion, which has no following load event.
          initProductRoot(root, lifetime.stickyTopBarRoot);
        }
      });
    });
    document.addEventListener('shopify:section:load', () => {
      window.SBProductMain.initProductScope(document);
    });
    window.SBProductMain.initProductScope(document);
})();
